import axios from 'axios'
import React, { FormEvent, Fragment, useEffect, useState } from 'react'
import { IoIosSearch, IoMdAttach, IoMdClose } from 'react-icons/io'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

import { useCardCategoryQuery } from '../../features/giftCard/cardCategorySlice'
import { GiftCard } from '../../components/Dashboard/giftcard/GiftCard'
import { transactionTableData } from '../../data/TransactionTableData'
import { useDispatch } from 'react-redux'
import { Loader } from '../../shared/Loader'
import { IGiftCard } from '../../components/Dashboard/giftcard/types'
import { useCardsQuery } from '../../features/giftCard/cardsSlice'
import { CardRateTableSkeleton } from '../../components/Dashboard/giftcard/CardRateTableSkeleton'
import { Link, useSearchParams } from 'react-router-dom'
import { TopHottestCards } from '../../components/Dashboard/homepage/TopHottestCards'

export const GiftCardPage = () => {
  // Modal states
  const [page, setPage] = useState<number>(1)
  const [searchParams] = useSearchParams()
  const pageFromUrl = searchParams.get('page')

  useEffect(() => {
    setPage(Number(pageFromUrl))
  }, [])

  const [ratePage, setRatePage] = useState<number>(1)
  const [search, setSearch] = useState<{
    temp: string
    name: string
  }>({
    temp: '',
    name: '',
  })

  const dispatch = useDispatch()
  const {
    data: categories,
    isLoading,
    isFetching,
    isError,
    error,
  } = useCardCategoryQuery({ page, limit: 6, name: search.name })

  const {
    data: cards,
    isLoading: isCardsLoading,
    isFetching: isCardsFetching,
    isError: isCardsError,
  } = useCardsQuery({ page: ratePage })

  const handleSearch = (e: any) => {
    e.preventDefault()
    setPage(1)

    setSearch({
      ...search,
      name: search.temp,
    })
  }

  return (
    <div className='font-dashboard pt-4 pb-12 px-4 min-h-screen'>
      <div className='container-lg relative'>
        {/* Search  ------------------------------------- */}
        <div className='mt-4 flex flex-col gap-4 md:flex-row md:justify-between'>
          <div className='bg-gray-600 rounded-lg overflow-hidden pl-4 flex-1 max-w-sm flex items-center justify-between gap-4'>
            <input
              type='search'
              name='search'
              id='search'
              placeholder='Search categories here...'
              value={search.temp}
              onChange={(e: FormEvent<HTMLInputElement>) => {
                setSearch({
                  ...search,
                  temp: e.currentTarget.value,
                })
                if (e.currentTarget.value === '') {
                  setSearch({
                    temp: e.currentTarget.value,
                    name: '',
                  })
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e)
                }
              }}
              className='focus:outline-none bg-transparent py-2 text-sm font-light text-white/70 placeholder:text-white/40 w-full'
            />
            <button
              onClick={handleSearch}
              className='transition-all duration-200 px-4 py-2 bg-zinc-600 hover:bg-zinc-500/50'
            >
              <IoIosSearch className='text-xl text-white/60 cursor-pointer' />
            </button>
          </div>
        </div>
        {/* Search end------------------------------------- */}

        <div className='lg:grid lg:grid-cols-12 flex flex-col gap-4 mt-8'>
          <div className='bg-black lg:col-span-9 px-4 py-5'>
            <h3 className='text-primary text-xl font-medium'>Categories</h3>
            {isLoading || isFetching ? (
              <div className='w-full h-[65vh] flex justify-center items-center'>
                <Loader />
              </div>
            ) : categories?.data.length == 0 ? (
              <div className='w-full bg-red font-light text-white/40 my-24 text-center'>
                No categories found
              </div>
            ) : (
              <div className='w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-8'>
                {categories?.data?.map((cardData: IGiftCard) => (
                  <GiftCard
                    cards={cardData.cards}
                    image={cardData.image}
                    name={cardData.name}
                    isDisabled={cardData.isDisabled}
                  />
                ))}
              </div>
            )}

            <div className='flex items-center gap-5 mt-12 ml-6'>
              <p className='text-white/60 font-light text-sm'>
                page: {pageFromUrl} | total: {categories?.total} categories
              </p>
              <div className='flex items-center gap-2'>
                <Link to={`/app/giftcard?page=${page - 1 || 1}`}>
                  <button
                    className='rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer'
                    disabled={pageFromUrl === '1'}
                    onClick={() => setPage(page - 1)}
                  >
                    <FiChevronLeft className='box-content p-3' />
                  </button>
                </Link>

                <Link
                  to={`/app/giftcard?page=${
                    page < categories?.last_page ? page + 1 : page
                  }`}
                >
                  <button
                    className='rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer'
                    disabled={pageFromUrl == categories?.last_page}
                    onClick={() => setPage(page + 1)}
                  >
                    <FiChevronRight className='box-content p-3' />
                  </button>
                </Link>
              </div>
            </div>
          </div>
          <div className='bg-black w-full lg:col-span-3 px-4 py-5 space-y-6'>
            
            {/* Card Rate Table */}
            <div>
              <h3 className='text-primary text-xl font-medium'>Card Rate</h3>

                <div className='overflow-x-auto relative mt-8'>
                  <table className='w-full text-sm text-left text-gray-400'>
                <thead className='text-xs uppercase bg-gray-700 text-gray-300'>
                  <tr>
                    <th scope='col' className='py-3 px-3'>
                      Card Name
                    </th>
                    <th scope='col' className='py-3 px-3'>
                      Per NGN Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isCardsLoading || isCardsFetching ? (
                    <CardRateTableSkeleton />
                  ) : (
                    cards?.data.map(
                      (card: { _id: string; name: string; rate: number }) => (
                        <tr className='border-b bg-transparent border-gray-700'>
                          <td className='py-4 px-4'>{card.name}</td>
                          <td className='py-4 px-4'>{card.rate}</td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>

              <div className='flex items-center gap-5 mt-12 ml-2'>
                <p className='text-white/60 font-light text-sm'>
                  page: {ratePage} | total: {cards?.total} cards
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    className='rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer'
                    disabled={ratePage === 1}
                    onClick={() => setRatePage(ratePage - 1)}
                  >
                    <FiChevronLeft className='box-content p-3' />
                  </button>
                  <button
                    className='rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer'
                    disabled={ratePage === Number(cards?.last_page)}
                    onClick={() => setRatePage(ratePage + 1)}
                  >
                    <FiChevronRight className='box-content p-3' />
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
